// Datetime utilities: ensure consistent timezone handling across the app

/**
 * UTC 시간값을 안전하게 Date 객체로 파싱합니다.
 * - 타임존 표시(Z 또는 ±HH:MM)가 없는 ISO 문자열은 UTC로 강제 해석합니다.
 * - Date 객체나 epoch 숫자는 그대로 Date 로 변환합니다.
 * @param {string|number|Date} value UTC 기준 시간값
 * @returns {Date|null} 유효하지 않으면 null
 */
export const parseUtc = (value) => {
  if (value == null || value === '') return null
  let v = value
  if (typeof value === 'string') {
    const hasZone = /[zZ]$|[+-]\d{2}:?\d{2}$/.test(value)
    v = hasZone ? value : value + 'Z'
  }
  const d = new Date(v)
  return Number.isNaN(d.getTime()) ? null : d
}

/**
 * 두 UTC 시간값 사이의 초(끝 − 시작)를 구합니다.
 * - 시험 입실·퇴실 기록(connections)의 소요 시간 `submitted_at − entered_at` 에 씁니다.
 * @param {string|number|Date} startUtc UTC 기준 시작 시각
 * @param {string|number|Date} endUtc UTC 기준 끝 시각
 * @returns {number|null} 초(정수). 둘 중 하나라도 없거나 유효하지 않으면 null
 */
export const diffUtcSeconds = (startUtc, endUtc) => {
  const start = parseUtc(startUtc)
  const end = parseUtc(endUtc)
  return start && end ? Math.round((end - start) / 1000) : null
}

/**
 * UTC 시간값(ISO/Date/epoch)을 받아 RFC3339(타임존 오프셋 포함) 문자열로 변환합니다.
 * - 유효하지 않은 값은 null을 반환합니다.
 * @param {string|number|Date} value UTC 기준 시간값 (ISO 문자열, Date, epoch ms)
 * @returns {string|null} RFC3339 형태의 문자열 (예: 2025-01-15T13:45:00+09:00) 또는 null
 */

export const toRfc3339OrNull = (value) => {
  if (!value) return null
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return null
  const pad = (n) => String(n).padStart(2, '0')
  const y = date.getFullYear()
  const m = pad(date.getMonth() + 1)
  const d = pad(date.getDate())
  const hh = pad(date.getHours())
  const mm = pad(date.getMinutes())
  const ss = pad(date.getSeconds())
  const tz = -date.getTimezoneOffset()
  const sign = tz >= 0 ? '+' : '-'
  const tzh = pad(Math.floor(Math.abs(tz) / 60))
  const tzm = pad(Math.abs(tz) % 60)
  return `${y}-${m}-${d}T${hh}:${mm}:${ss}${sign}${tzh}:${tzm}`
}

/**
 * 입력값을 datetime-local 인풋에 맞는 로컬 문자열(YYYY-MM-DDTHH:mm)로 변환합니다.
 * - 타임존 포함 입력은 로컬로 변환
 * - 타임존 없는 naive ISO는 그대로 잘라서 반환
 * - 그 외 포맷은 Date 파싱 후 로컬로 변환
 * @param {string|number|Date} value
 * @returns {string} 빈 문자열 또는 YYYY-MM-DDTHH:mm
 */
export const toDatetimeLocalOrEmpty = (value) => {
  if (!value) return ''
  const s = String(value)
  const hasZone = /[zZ]$|[+-]\d{2}:?\d{2}$/.test(s)
  const isNaiveIso = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2})?$/.test(s)
  // 1) 타임존이 포함된 경우 → 로컬로 변환
  if (hasZone) {
    const date = new Date(s)
    if (Number.isNaN(date.getTime())) return ''
    const pad = (n) => String(n).padStart(2, '0')
    const y = date.getFullYear()
    const m = pad(date.getMonth() + 1)
    const d = pad(date.getDate())
    const hh = pad(date.getHours())
    const mm = pad(date.getMinutes())
    return `${y}-${m}-${d}T${hh}:${mm}`
  }
  // 2) 타임존이 없는 naive ISO 문자열 → 로컬로 가정하고 초만 제거
  if (isNaiveIso) {
    return s.slice(0, 16) // YYYY-MM-DDTHH:mm
  }
  // 3) 기타 포맷은 파싱 시도 후 로컬로 변환
  const date = new Date(s)
  if (Number.isNaN(date.getTime())) return ''
  const pad = (n) => String(n).padStart(2, '0')
  const y = date.getFullYear()
  const m = pad(date.getMonth() + 1)
  const d = pad(date.getDate())
  const hh = pad(date.getHours())
  const mm = pad(date.getMinutes())
  return `${y}-${m}-${d}T${hh}:${mm}`
}

/**
 * 한국어 로캘로 날짜+시간을 포맷합니다.
 * @param {string|number|Date} value
 * @returns {string}
 */
export const formatKoreanDateTime = (value) => {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleString('ko-KR')
}

/**
 * 한국어 로캘로 날짜만 포맷합니다.
 * @param {string|number|Date} value
 * @returns {string}
 */
export const formatKoreanDate = (value) => {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleDateString('ko-KR')
}

/**
 * 한국어 로캘로 시간(HH:mm)만 포맷합니다.
 * @param {string|number|Date} value
 * @returns {string}
 */
export const formatKoreanTime = (value) => {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' })
}

/**
 * 한국어 로캘로 시간 범위를 포맷합니다.
 * - 같은 날이면 "HH:mm - HH:mm"
 * - 다른 날이면 "M월 D일 HH:mm - M월 D일 HH:mm" 유사 표현
 * @param {string|number|Date} start
 * @param {string|number|Date} end
 * @returns {string}
 */
export const formatTimeRangeKorean = (start, end) => {
  if (!start || !end) return ''
  const s = new Date(start)
  const e = new Date(end)
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return ''
  const sameDay = s.toDateString() === e.toDateString()
  if (sameDay) return `${formatKoreanTime(s)} - ${formatKoreanTime(e)}`
  const sd = s.toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' })
  const ed = e.toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' })
  return `${sd} ${formatKoreanTime(s)} - ${ed} ${formatKoreanTime(e)}`
}

/** 화면 표시 기준 시간대. 관리자 화면은 접속 위치와 무관하게 이 시간대로 보여준다. */
export const DISPLAY_TZ = 'Asia/Seoul'

/**
 * 표기 없는 서버 시각을 KST 로 해석한다(관리자 화면 기본값).
 * 회원 가입일·클래스 생성일·수강생 신청일·결제일 등 대부분의 값이 여기 해당한다.
 */
export const NAIVE_AS_KST = 'kst'

/**
 * 표기 없는 서버 시각의 시간대를 "알 수 없음" 으로 둔다.
 * **학습활동 3종 전용**이다. UTC 통일(2026-09-11) 이전 학습활동 시각은 학생 기기의
 * 로컬 시각이 표기 없이 저장된 값이라, KST 로 단정하면 최대 열몇 시간이 틀어진다.
 * 다른 도메인에 쓰면 지금까지 맞던 KST 표시가 "시간대 미상" 으로 잘못 보인다.
 */
export const NAIVE_AS_UNKNOWN = 'unknown'

/**
 * 서버 시각 문자열을 "시간대를 아는지" 여부와 함께 파싱합니다.
 *
 * 시간대 판별 규칙(백엔드 2026-09-11 회신 기준.
 * docs/시각표기_시간대_규칙.md §1):
 * - 오프셋(`Z`/`±HH:MM`) 포함 → 그대로 신뢰. 학습활동 3종·스케줄러 로그·접근 로그가 해당
 * - `KST`/`JST` 접미 → +09:00, `UTC`/`GMT` 접미 → +00:00 (구 `feedbackUpdatedAt` 형식)
 * - 표기 없음 → 기본은 **KST**(회원·클래스·쿠폰·알림·결제).
 *   학습활동 3종만 `naiveZone: NAIVE_AS_UNKNOWN` 을 넘겨 "알 수 없음" 으로 처리한다
 *
 * @param {string|number|Date} value 서버가 준 시각값
 * @param {{naiveZone?: string}} [options] 표기 없는 값의 해석 기준
 * @returns {{date: Date|null, zoneKnown: boolean}}
 *   zoneKnown 이 false 인 Date 는 브라우저 로컬로 해석한 값이다.
 *   벽시계 숫자가 보존되므로 timeZone 옵션 없이 포맷해야 문자열에 적힌 그대로 표시된다.
 */
export const parseServerDateTime = (value, { naiveZone = NAIVE_AS_KST } = {}) => {
  const EMPTY = { date: null, zoneKnown: false }
  if (value == null || value === '') return EMPTY
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? EMPTY : { date: value, zoneKnown: true }
  }
  if (typeof value === 'number') {
    const d = new Date(value)
    return Number.isNaN(d.getTime()) ? EMPTY : { date: d, zoneKnown: true }
  }

  // 마이크로초(6자리)는 브라우저마다 파싱이 흔들려 밀리초 3자리로 줄인다
  const s = String(value).trim().replace(/(\.\d{3})\d+/, '$1')

  // 구 형식: "2026-03-31 15:50:45 KST"
  const abbr = s.match(/^(.*?)\s+(KST|JST|UTC|GMT)$/i)
  if (abbr) {
    const offset = /KST|JST/i.test(abbr[2]) ? '+09:00' : 'Z'
    const d = new Date(abbr[1].trim().replace(' ', 'T') + offset)
    return Number.isNaN(d.getTime()) ? EMPTY : { date: d, zoneKnown: true }
  }

  const iso = s.replace(' ', 'T')
  if (/[zZ]$|[+-]\d{2}:?\d{2}$/.test(iso)) {
    const d = new Date(iso)
    return Number.isNaN(d.getTime()) ? EMPTY : { date: d, zoneKnown: true }
  }

  if (naiveZone === NAIVE_AS_KST) {
    const withTime = /^\d{4}-\d{2}-\d{2}$/.test(iso) ? iso + 'T00:00:00' : iso
    const d = new Date(withTime + '+09:00')
    return Number.isNaN(d.getTime()) ? EMPTY : { date: d, zoneKnown: true }
  }

  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? EMPTY : { date: d, zoneKnown: false }
}

/**
 * 서버 시각을 화면용 문자열로 포맷합니다.
 * - 시간대를 아는 값: KST(기본)로 변환해 표시
 * - 시간대를 모르는 값: 서버 문자열의 벽시계 숫자를 그대로 표시(임의 변환 금지)
 *
 * @param {string|number|Date} value 서버가 준 시각값
 * @param {Intl.DateTimeFormatOptions} [dtfOptions] 포맷 옵션 (timeZone 을 주면 그것이 우선)
 * @param {{naiveZone?: string}} [options] 표기 없는 값의 해석 기준
 * @returns {string} 포맷 문자열. 파싱 실패 시 빈 문자열
 */
export const formatServerDateTime = (value, dtfOptions = {}, options = {}) => {
  const { date, zoneKnown } = parseServerDateTime(value, options)
  if (!date) return ''
  const opts = zoneKnown ? { timeZone: DISPLAY_TZ, ...dtfOptions } : { ...dtfOptions }
  try {
    return new Intl.DateTimeFormat('ko-KR', opts).format(date)
  } catch {
    return ''
  }
}

/**
 * 서버 시각을 `YYYY-MM-DD HH:mm:ss` 로 포맷합니다(로그·상세 화면용).
 * - 시간대를 아는 값: KST 로 변환
 * - 시간대를 모르는 값: 서버 문자열의 벽시계 숫자를 그대로 (마이크로초만 잘라냄)
 *
 * @param {string|number|Date} value 서버가 준 시각값
 * @param {{naiveZone?: string}} [options] 표기 없는 값의 해석 기준
 * @returns {string} `YYYY-MM-DD HH:mm:ss`. 파싱 실패 시 빈 문자열
 */
export const formatServerClock = (value, options = {}) => {
  const { date, zoneKnown } = parseServerDateTime(value, options)
  if (!date) return ''
  if (zoneKnown) return formatUtcToTimeZone(date, DISPLAY_TZ).replace('T', ' ')
  return String(value).trim().replace('T', ' ').replace(/\.\d+$/, '')
}

/**
 * UTC 시간과 IANA 타임존을 입력받아 해당 타임존의 현지 시간 ISO(YYYY-MM-DDTHH:mm:ss) 문자열을 반환합니다.
 * - 반환 문자열에는 타임존 오프셋을 포함하지 않습니다(표시용). 오프셋이 필요하면 별도 옵션/함수를 고려하세요.
 * - 브라우저의 Intl API를 사용하여 신뢰 가능한 타임존 변환을 수행합니다.
 * @param {string|number|Date} utcValue UTC 기준 시간값 (ISO 문자열, Date, epoch ms)
 * @param {string} timeZone IANA 타임존(ex. "Asia/Seoul", "America/New_York")
 * @returns {string} YYYY-MM-DDTHH:mm:ss (해당 타임존 현지 시각, 오프셋 미포함). 유효하지 않으면 빈 문자열.
 */
export const formatUtcToTimeZone = (utcValue, timeZone) => {
  if (!utcValue || !timeZone) return ''
  const date = parseUtc(utcValue)
  if (!date) return ''
  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    })
    const parts = formatter.formatToParts(date).reduce((acc, p) => {
      acc[p.type] = p.value
      return acc
    }, {})
    // parts: { year, month, day, hour, minute, second }
    return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}:${parts.second}`
  } catch {
    return ''
  }
}

/**
 * 현지 시간과 IANA 타임존을 입력받아 UTC ISO 문자열을 반환합니다.
 * - formatUtcToTimeZone의 역변환 함수입니다.
 * - 시험 등록/수정 시 현지 시간을 UTC로 변환하여 서버에 전송할 때 사용합니다.
 * @param {string} localValue 현지 시간 문자열 (YYYY-MM-DDTHH:mm 또는 YYYY-MM-DDTHH:mm:ss)
 * @param {string} timeZone IANA 타임존(ex. "Asia/Seoul", "America/New_York")
 * @returns {string} UTC ISO 문자열 (YYYY-MM-DDTHH:mm:ssZ). 유효하지 않으면 빈 문자열.
 */
export const formatLocalToUtc = (localValue, timeZone) => {
  if (!localValue || !timeZone) return ''
  try {
    // 현지 시간 문자열 파싱
    const [datePart, timePart] = localValue.split('T')
    if (!datePart || !timePart) return ''

    const [year, month, day] = datePart.split('-').map(Number)
    const [hour, minute, second = 0] = timePart.split(':').map(n => parseInt(n, 10) || 0)

    // 해당 타임존의 오프셋을 계산하기 위해 임시 날짜 생성
    // Intl.DateTimeFormat을 사용하여 타임존 오프셋 추출
    const tempDate = new Date(Date.UTC(year, month - 1, day, hour, minute, second))

    // 타임존의 현지 시간을 구함
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    })

    // UTC 시간을 해당 타임존으로 변환했을 때의 차이 계산
    const parts = formatter.formatToParts(tempDate).reduce((acc, p) => {
      acc[p.type] = parseInt(p.value, 10)
      return acc
    }, {})

    // 타임존 변환으로 인한 차이 계산
    const tzYear = parts.year
    const tzMonth = parts.month
    const tzDay = parts.day
    const tzHour = parts.hour === 24 ? 0 : parts.hour // 24시를 0시로 변환
    const tzMinute = parts.minute
    const tzSecond = parts.second

    // 원하는 현지 시간과 타임존 변환된 시간의 차이를 구함
    const targetLocal = new Date(year, month - 1, day, hour, minute, second)
    const convertedLocal = new Date(tzYear, tzMonth - 1, tzDay, tzHour, tzMinute, tzSecond)

    // 차이만큼 UTC 시간 조정
    const diffMs = targetLocal.getTime() - convertedLocal.getTime()
    const utcDate = new Date(tempDate.getTime() + diffMs)

    // UTC ISO 문자열 반환
    const pad = (n) => String(n).padStart(2, '0')
    return `${utcDate.getUTCFullYear()}-${pad(utcDate.getUTCMonth() + 1)}-${pad(utcDate.getUTCDate())}T${pad(utcDate.getUTCHours())}:${pad(utcDate.getUTCMinutes())}:${pad(utcDate.getUTCSeconds())}Z`
  } catch {
    return ''
  }
}

/**
 * 일반적으로 자주 사용하는 IANA 타임존 목록입니다.
 */
export const COMMON_TIMEZONES = [
  'UTC',
  'Asia/Seoul',
  'Asia/Tokyo',
  'Asia/Shanghai',
  'Asia/Hong_Kong',
  'Asia/Singapore',
  'Asia/Taipei',
  'Asia/Bangkok',
  'Asia/Kolkata',
  'Europe/London',
  'Europe/Berlin',
  'Europe/Paris',
  'Europe/Madrid',
  'Europe/Moscow',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Sao_Paulo',
  'Australia/Sydney',
  'Pacific/Auckland'
]